import { createPortal } from "react-dom";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import sanitizeHtml from "sanitize-html";
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridAggregationModel,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  Toolbar,
  ToolbarButton,
  gridColumnLookupSelector,
  gridColumnReorderDragColSelector,
  gridRowGroupingSanitizedModelSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import moment from "moment";
import StatusCell from "./Cells/StatusCell";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import IconsCell from "./Cells/IconsCell";
import MenuCell from "./Cells/MenuCell";
import { plGridLocales } from "utils/muiGridLocales";
import { nullHandlingSortComparator } from "utils/nullHandlingSortComparator";
import Spinner from "components/Spinner";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import PreviewModal from "components/PreviewModal";
import { ItemKind } from "dto/IKindItems";
import SellerCell from "./Cells/SellerCell";
import BuyerCell from "./Cells/BuyerCell";
import CopyableValue from "./Cells/CopyableValue";
import registryInvoicesService from "api/registryInvoicesService";
import { IRegistryInvoice } from "dto/Invoices/IRegistryInvoice";
import InfoIcon from "assets/icons/info_icon.svg?react";
import FilterIcon from "assets/icons/tune.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import InvoiceMetricSlide from "components/Budget/InvoiceMetricSlide";
import { generateInvoicesPdf } from "utils/generateInvoicesPdf";
import useGridRowSelection from "hooks/useGridRowSelection";
import DocTypeSelect from "components/Registry/DocTypeSelect";
import { docScopeOf, docSubtitleLabel, DEFAULT_DOC_TYPE } from "components/Registry/DocTypeSelect/docTypes";
import PeriodPicker from "components/Registry/PeriodPicker";
import { useSchemeMatcher } from "hooks/useSchemeMatcher";

// ── Stałe ────────────────────────────────────────────────────────────────────

const GRID_KEY = "grid_ksef_invoices_v1";
const LS_FILTERS_KEY = "ksefInvoices_savedFilters";
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => CURRENT_YEAR - i);
const BRAK = "Brak";

const SCOPE_OPTIONS = [
  { value: 0, label: "Sprzedaż" },
  { value: 1, label: "Zakup" },
  { value: 2, label: "Inne podmioty" },
  { value: 3, label: "Uprawniony podmiot" },
] as const;
type ScopeValue = 0 | 1 | 2 | 3;

interface SavedFilter { Id: string; Name: string; }
interface InvoiceFilters { seller: string[]; buyer: string[]; typ: string[]; }
const EMPTY_FILTERS: InvoiceFilters = { seller: [], buyer: [], typ: [] };

const BRAK_STR = "Brak";
const buildOptions = (values: string[]): string[] => {
  const nonEmpty = [...new Set(values.filter((v) => v !== ""))].sort((a, b) => a.localeCompare(b, "pl"));
  const hasEmpty = values.some((v) => v === "");
  if (nonEmpty.length === 0) return [BRAK_STR];
  return hasEmpty ? [...nonEmpty, BRAK_STR] : nonEmpty;
};

type PopupPos = { top: number; left: number } | null;

const toISO = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayISO = () => toISO(new Date());
const plDate = (iso: string) => iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

// ── Komponent ─────────────────────────────────────────────────────────────────

const KSeFInvoiceRegister: FC<Props> = ({
  searchText,
  setSubtitle,
  docType = DEFAULT_DOC_TYPE,
  onDocTypeChange,
}) => {
  const CustomNoRowsOverlay = () => {
    return (
      <div className={styles.empty}>
        <img src={Search} />
        <div>Brak wyników</div>
      </div>
    );
  };

  const wrapperRef = useRef(null);
  const [invoices, setInvoices] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [prevPage, setPrevPage] = useState<number | null>(null);
  const [, setNextPage] = useState(0);
  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const [metricOf, setMetricOf] = useState<IRegistryInvoice | null>(null);
  // Zakres faktur wyprowadzony z „Typ dokumentu" (np. "inv:1" → 1 = Zakup).
  // "inv:all" = Wszystkie — bez zawężania do jednego zakresu (dane lokalne).
  const isAllInvoices = docScopeOf(docType) === "all";
  const parsedScope = Number(docScopeOf(docType));
  const listFilter: ScopeValue = ([0, 1, 2, 3].includes(parsedScope) ? parsedScope : 1) as ScopeValue;

  const { data: localInvoicesData = [] } = registryInvoicesService.useGetInvoices(searchText);

  // Audyt schematów księgowania — czy dla kontrahenta pozycji istnieje schemat.
  const matchScheme = useSchemeMatcher();
  // Charakter pozycji → (NIP kontrahenta, kierunek). Faktura zakupu: kontrahent =
  // sprzedawca → „Purchase". Faktura sprzedaży: kontrahent = nabywca → „Sale".
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowHasScheme = (row: any): boolean => {
    if (!row) return false;
    if (row.SourceType === "K") {
      if (row.SubjectType === 0) return matchScheme({ nip: row.Buyer?.Identifier?.Value, direction: "Sale" });
      if (row.SubjectType === 1) return matchScheme({ nip: row.Seller?.Nip, direction: "Purchase" });
      return false; // inne podmioty / uprawniony — poza zakresem audytu
    }
    // Faktury/rachunki rejestrowe = zakupowe.
    return matchScheme({ nip: row.Seller?.Nip, direction: "Purchase" });
  };

  const statuses = useMemo(
    () => statusesData?.data ?? [],
    [statusesData?.data],
  );
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const getStatus = (item: ICaseSimple) => {
    if (!item.StatusId) return null;
    const s = statuses.find((x) => x.Id == item.StatusId);
    if (s) return s.Name;
    return null;
  };

  function formatPLN(locale, currency, number) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    })
      .formatToParts(number)
      .filter((x) => x.type !== "currency")
      .filter((x) => x.type !== "literal")
      .map((x) => x.value)
      .join("");
  }

  // ── Stan paska funkcji ───────────────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [preset, setPreset] = useState<string | null>("biezacyRok");
  const [from, setFrom] = useState(`${CURRENT_YEAR}-01-01`);
  const [to, setTo] = useState(todayISO());
  const [periodPopupPos, setPeriodPopupPos] = useState<PopupPos>(null);
  const [savedFiltersPopupPos, setSavedFiltersPopupPos] = useState<PopupPos>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<InvoiceFilters>(EMPTY_FILTERS);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    try { setSavedFilters(JSON.parse(localStorage.getItem(LS_FILTERS_KEY) || "[]")); } catch { /* */ }
  }, []);

  const persistFilters = (list: SavedFilter[]) => {
    setSavedFilters(list);
    localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(list));
  };

  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleFilter = (field: keyof InvoiceFilters, value: string) =>
    setActiveFilters((prev) => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });

  const activeFilterCount = useMemo(() =>
    (activeFilters.seller.length > 0 ? 1 : 0) +
    (activeFilters.buyer.length > 0 ? 1 : 0) +
    (activeFilters.typ.length > 0 ? 1 : 0),
  [activeFilters]);

  const columns: GridColDef[] = [
    {
      field: "Year",
      width: 70,
      headerName: "Rok",
      groupable: true,
      valueGetter: (value, row) => row.IssueDate,
      renderHeader: () => {
        return <div className={styles.header}>Rok</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yyyy");
        }
        return null;
      },
    },

    {
      field: "Month",
      width: 70,
      headerName: listFilter === 0 ? "Miesiąc sprzedaży" : "Miesiąc zakupu",
      groupable: true,
      renderHeader: () => {
        return <div className={styles.header}>Miesiąc</div>;
      },
      valueGetter: (value, row) => row.IssueDate,
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("MM");
        }
        return null;
      },
    },
    {
      field: "IssueDate",
      width: 100,
      headerName: "Data wystawienia",
      renderHeader: () => {
        return <div className={styles.header}>Wystawiono</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("DD.MM.yyyy");
        }
        return null;
      },
      groupingValueGetter: (value, row) =>
        moment(row.IssueDate).format("yyyy-MM"),
      renderCell: (params: GridRenderCellParams<any, string>) => {
        const isGroupRow = params.rowNode.type === "group";
        const isGroupingColumn = params.rowNode.groupingField === "IssueDate";

        if (!isGroupRow && !isGroupingColumn)
          return <>{moment(params.value).format("DD.MM.yyyy")}</>;
        if (isGroupingColumn) return <>{params.value}</>;

        return null;
      },
    },

    {
      // Data płatności — bezpośrednio po „Data wystawienia", dla każdego zakresu.
      // Etykieta w gridzie: „Płatność".
      field: "PaymentDate",
      width: 100,
      headerName: "Data płatności",
      groupable: false,
      renderHeader: () => {
        return <div className={styles.header}>Płatność</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("DD.MM.yyyy");
        }
        return null;
      },
    },

    {
      field: "InvoiceNumber",
      minWidth: 140,
      headerName: "Numer",
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>Numer</div>;
      },
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) => (
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(params.value) }}
        ></div>
      ),
    },

    {
      field: "SourceType",
      width: 60,
      headerName: "Typ dokumentu",
      sortable: true,
      groupable: true,
      renderHeader: () => <div className={styles.header}>Typ</div>,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") return <>{params.value}</>;
        const type = params.value as "K" | "F" | "R";
        if (!type) return null;
        const isPartial = !!(params.row as IRegistryInvoice).IsPartial;
        const tooltips: Record<string, string> = { K: "faktura KSeF", F: "Faktura", R: "Rachunek" };
        const colors: Record<string, string> = { K: "#1565c0", F: "#2e7d32", R: "#e65100" };
        const label = isPartial ? `${tooltips[type] ?? type} — dane niekompletne` : (tooltips[type] ?? "");
        return (
          <Tooltip title={label}>
            <span style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: colors[type] ?? "#888",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                cursor: "default",
              }}>
                {type}
              </span>
              {isPartial && (
                <span style={{
                  position: "absolute",
                  top: -5,
                  right: -7,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "#f59e0b",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 900,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  border: "1.5px solid #fff",
                }}>
                  !
                </span>
              )}
            </span>
          </Tooltip>
        );
      },
    },

    {
      field: "Seller",
      minWidth: 200,
      flex: 1,
      headerName: "Sprzedawca",
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Seller?.Name,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <SellerCell element={params.row}></SellerCell>
        ) : (
          <>{params.value}</>
        ),
    },

    {
      field: "Buyer",
      minWidth: 200,
      flex: 1,
      headerName: "Nabywca",
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Buyer?.Name,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <BuyerCell element={params.row}></BuyerCell>
        ) : (
          <>{params.value}</>
        ),
    },

    {
      field: "Status",
      width: 140,
      headerName: "Status",
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.nameCell}`,
      type: "string",
      valueGetter: (value, row) => getStatus(row),
      groupingValueGetter: (value, row) => getStatus(row) ?? "Brak statusu",
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<any, string>) =>
        params.rowNode.type !== "group" ? (
          <StatusCell
            element={params.row}
            statuses={statuses}
            apiRef={apiRef}
          ></StatusCell>
        ) : (
          <div>{params.value}</div>
        ),
    },

    {
      field: "NetAmount",
      minWidth: 120,
      headerName: "Netto",
      cellClassName: `${styles.nameCell}`,
      align: "right",
      aggregable: true,
      type: "number",
      valueFormatter: (value) =>
        value ? formatPLN("pl-PL", "PLN", value) : null,
    },
    {
      field: "VatAmount",
      minWidth: 120,
      headerName: "VAT",
      cellClassName: `${styles.nameCell}`,
      align: "right",
      aggregable: true,
      type: "number",
      valueFormatter: (value) =>
        value ? formatPLN("pl-PL", "PLN", value) : null,
    },
    {
      field: "GrossAmount",
      minWidth: 100,
      headerName: "Brutto",
      align: "right",
      aggregable: true,
      cellClassName: `${styles.nameCell}`,
      type: "number",
      valueFormatter: (value) =>
        value ? formatPLN("pl-PL", "PLN", value) : null,
    },

    {
      field: "Currency",
      minWidth: 50,
      headerName: "Waluta",
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Currency,
    },

    {
      field: "BankAccount",
      width: 120,
      headerName: listFilter === 0 ? "Rachunek nabywcy" : "Rachunek sprzedawcy",
      groupable: false,
      renderHeader: () => {
        return <div className={styles.header}>Rachunek</div>;
      },
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") return <>{params.value}</>;
        const acc = String(params.value ?? "").trim();
        if (!acc) return null;
        return (
          <CopyableValue
            display={acc}
            copyValue={acc.replace(/\D/g, "") || acc}
            label="Kopiuj numer rachunku"
          />
        );
      },
    },

    {
      field: "Icons",
      width: 80,
      headerName: "",
      disableReorder: true,
      disableColumnMenu: true,
      sortable: false,
      groupable: false,
      cellClassName: `${styles.iconsCell}`,

      renderCell: (params: GridRenderCellParams<any, number>) => {
        if (params.rowNode.type === "group") return null;
        const hasScheme = rowHasScheme(params.row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {hasScheme && (
              <Tooltip title="Schemat księgowania ustalony dla tego kontrahenta" placement="left">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#2e7d32",
                    boxShadow: "0 0 0 2px #d5f0d8",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <IconsCell
              element={params.row}
              statuses={statuses}
              apiRef={apiRef}
            ></IconsCell>
            {/* Ikona metryki (przeniesiona z kolumny „Met.") — pierwsza od prawej. */}
            {params.row?.Id && (
              <Tooltip title="Metryka" placement="left">
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                  onClick={() => setMetricOf(params.row)}
                >
                  <InfoIcon style={{ width: 18, height: 18 }} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },

    {
      field: "Menu",
      width: 40,
      headerName: "",
      renderHeader: () => <div></div>,
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <MenuCell
            element={params.row}
            apiRef={apiRef}
            reload={reload}
          ></MenuCell>
        ) : (
          <></>
        ),
    },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>({});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>([]);

  const columnsMemo = useMemo(() => columns, [tags, statuses, listFilter, matchScheme]);

  const localRows = useMemo(() => {
    if (listFilter !== 1 && !isAllInvoices) return [];
    return localInvoicesData.map((inv) => ({
      id: inv.Id,
      ...inv,
      SourceType: inv.DocumentType === "Invoice" ? ("F" as const) : ("R" as const),
      Seller: { Name: inv.IssuerCompany || inv.IssuerFullName || "", Nip: inv.IssuerNIP || inv.IssuerPESEL || "" },
      NetAmount: inv.NetAmount ?? inv.Amount,
      GrossAmount: inv.GrossAmount ?? inv.Amount,
      Currency: "PLN",
      BankAccount: inv.AccountNumber,
    }));
  }, [localInvoicesData, listFilter, isAllInvoices]);

  const allRows = useMemo(() => {
    const ksefRows = invoices.map((i) => ({ id: i.Id, ...i, SourceType: "K" as const }));
    const combined = [...ksefRows, ...localRows];
    return combined.filter((row) => {
      const issueDate = (row as any).IssueDate as string | undefined;
      if (from && issueDate && issueDate < from) return false;
      if (to && issueDate && issueDate > `${to}T23:59:59`) return false;
      if (activeFilters.seller.length > 0) {
        const val = (row as any).Seller?.Name || "";
        if (!activeFilters.seller.includes(val) && !(activeFilters.seller.includes(BRAK_STR) && !val)) return false;
      }
      if (activeFilters.buyer.length > 0) {
        const val = (row as any).Buyer?.Name || "";
        if (!activeFilters.buyer.includes(val) && !(activeFilters.buyer.includes(BRAK_STR) && !val)) return false;
      }
      if (activeFilters.typ.length > 0) {
        const val = (row as any).SourceType || "";
        if (!activeFilters.typ.includes(val)) return false;
      }
      return true;
    });
  }, [invoices, localRows, from, to, activeFilters]);

  const filterOptions = useMemo(() => {
    const ksefRows = invoices.map((i) => ({ ...i, SourceType: "K" as const }));
    const combined = [...ksefRows, ...localRows];
    return {
      seller: buildOptions(combined.map((r: any) => r.Seller?.Name || "")),
      buyer: buildOptions(combined.map((r: any) => r.Buyer?.Name || "")),
      typ: buildOptions(combined.map((r: any) => r.SourceType || "")),
    };
  }, [invoices, localRows]);

  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const handleRowDoubleClick = (itm: any) => {
    setSelectedInvoice(itm);
    setShowPreview(true);
  };

  const {
    data,
    fetchNextPage,
    isLoading,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["ksefRegistry", searchText, docType],
    async ({ pageParam = 0 }) => {
      // Pobierz jeden zakres (SubjectType) — „List" w BE to pojedynczy enum.
      const fetchScope = async (scope: number) => {
        const query = new URLSearchParams(window.location.search);
        if (searchText.length) query.append("Text", searchText);
        query.append("Page", pageParam);
        query.append("SortProp", "Date");
        query.append("SortOrder", "Descending");
        query.append("List", scope.toString());
        const res = await axiosInstance.get(`/ksef/invoices?${query}`);
        return res.data;
      };

      // „Wszystkie" (inv:all) = wszystkie zakresy równolegle (Sprzedaż/Zakup/
      // Inne podmioty/Uprawniony podmiot); pojedynczy zakres — jedno zapytanie.
      const scopes = isAllInvoices ? [0, 1, 2, 3] : [listFilter];
      const results = await Promise.all(scopes.map(fetchScope));

      const invoices = results.flatMap((r) => r?.Invoices ?? []);
      const hasMore = results.some((r) => r?.HasMore);

      return {
        items: { Invoices: invoices, Page: pageParam, HasMore: hasMore },
        page: pageParam,
        hasMore,
        nextPage: pageParam + 1,
      };
    },

    {
      staleTime: 0,
      cacheTime: 0,
      enabled: false,

      getPreviousPageParam: (firstPage) => {
        if (firstPage?.page > 0) {
          return firstPage.page - 1;
        }
        return undefined;
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.hasMore) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    },
  );

  useEffect(() => {
    setNextPage(0);
    setTimeout(() => {
      refetch();
    }, 100);
  }, [searchText, docType]);

  const reload = () => {
    setNextPage(0);
    setTimeout(() => {
      refetch();
    }, 100);
  };

  const compileData = async () => {
    if (!data) return;

    setNextPage(data.pages[data.pages.length - 1].nextPage);
    if (!prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }

    if (prevPage && data.pages[data.pages.length - 1].page <= prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }
    const packs = await Promise.all(
      data?.pages
        .sort((a, b) => {
          return a.page - b.page;
        })
        .map((page) => {
          return page.items.Invoices;
        })
        .flat(),
    );

    setInvoices(packs);
    setIsInitialized(true);
  };

  const apiRef = useGridApiRef();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  rowsRef.current = allRows;

  const gridSettingsStorageKey = () => `grid_ksef_${listFilter}`;

  const saveSnapshotInternal = () => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(
        gridSettingsStorageKey(),
        JSON.stringify(currentState),
      );
    }
  };

  const saveSnapshot = useCallback(() => {
    saveSnapshotInternal();
  }, [apiRef]);

  useEffect(() => {
    compileData();
  }, [data]);

  const [initialState, setInitialState] = useState<GridInitialState>(null);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(
      gridSettingsStorageKey(),
    );
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({} as GridInitialStatePremium);
    data.pinnedColumns = { right: ["Icons", "Menu"] };
    setInitialState({} as GridInitialStatePremium);
    setColumnVisibilityModel(data?.columns?.columnVisibilityModel);
    setGroupingModel(data.rowGrouping?.model ?? []);

    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const hiddenFields = [
    "Label",
    "IsFolder",
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    "Icons",
    "Menu",
  ];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  useEffect(() => {
    const scope = docSubtitleLabel(docType);
    const period = from || to ? `${plDate(from)} – ${plDate(to)}` : "";
    setSubtitle(period ? `${scope} · ${period}` : scope);
  }, [docType, from, to, setSubtitle]);

  useEffect(() => {
    if (isInitialized && initialState) {
      const settingsKey = gridSettingsStorageKey();
      const stateFromLocalStorage = localStorage?.getItem(settingsKey);
      const data: GridInitialStatePremium = stateFromLocalStorage
        ? JSON.parse(stateFromLocalStorage)
        : ({} as GridInitialStatePremium);
      setInitialState(data);
      setColumnVisibilityModel(data?.columns?.columnVisibilityModel ?? {});
      setGroupingModel(data.rowGrouping?.model ?? []);
    }
  }, [listFilter]);

  useEffect(() => {
    if (groupingModel.length > 0) {
      setAggregationModel({
        VatAmount: "sum",
        NetAmount: "sum",
        GrossAmount: "sum",
      });
    } else {
      setAggregationModel({});
    }
  }, [groupingModel]);

  const handleDownload = async () => {
    if (downloading) return;
    const selected = apiRef.current.getSelectedRows();
    const selectedIds = new Set(selected.keys());
    const toExport = allRows.filter((r) => selectedIds.size === 0 || selectedIds.has(r.id));
    if (toExport.length === 0) return;
    setDownloading(true);
    try {
      await generateInvoicesPdf({
        rows: toExport.map((r: any) => ({
          issueDate: r.IssueDate ?? "",
          invoiceNumber: r.InvoiceNumber ?? "",
          sourceType: r.SourceType ?? "",
          seller: r.Seller?.Name ?? "",
          buyer: r.Buyer?.Name ?? "",
          netAmount: r.NetAmount ?? r.Amount ?? 0,
          vatAmount: r.VatAmount ?? 0,
          grossAmount: r.GrossAmount ?? r.Amount ?? 0,
          currency: r.Currency ?? "PLN",
        })),
        from, to,
        scopeLabel: SCOPE_OPTIONS.find((o) => o.value === listFilter)?.label ?? "",
      });
    } finally { setDownloading(false); }
  };

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const KSeFInvoiceToolbar = () => {
    const gridApiRef = useGridApiContext();
    const rowGroupingModel = useGridSelector(gridApiRef, gridRowGroupingSanitizedModelSelector);
    const columnsLookup    = useGridSelector(gridApiRef, gridColumnLookupSelector);
    const draggedColumn    = useGridSelector(gridApiRef, gridColumnReorderDragColSelector);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (draggedColumn && !rowGroupingModel.includes(draggedColumn) && columnsLookup[draggedColumn]?.groupable !== false)
        gridApiRef.current.addRowGroupingCriteria(draggedColumn);
    };

    const removeGroup = (field: string) => {
      if (columnsLookup[field]?.groupable !== false)
        gridApiRef.current.removeRowGroupingCriteria(field);
    };

    const openPopup = (e: React.MouseEvent, setter: (p: PopupPos) => void, current: PopupPos) => {
      if (current) { setter(null); return; }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setter({ top: rect.bottom + 4, left: rect.left });
    };

    return (
      <Toolbar>
        <div className={styles.mergedToolbar}>
          <div className={styles.groupingSection} onDragOver={handleDragOver}>
            <MoveUpIcon fontSize="small" color="action" sx={{ mr: 0.75, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
              {rowGroupingModel.length === 0 ? (
                <Typography variant="body2" color="textSecondary" noWrap>
                  Przeciągnij kolumnę tutaj aby zgrupować
                </Typography>
              ) : (
                rowGroupingModel.map((field, idx) => (
                  <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                    {idx > 0 && <ChevronRightIcon fontSize="small" color="action" />}
                    <Chip label={columnsLookup[field]?.headerName ?? field} size="small" onDelete={() => removeGroup(field)} sx={{ cursor: "grab" }} />
                  </Box>
                ))
              )}
            </Box>
          </div>

          <div className={styles.toolbarRight}>
            <Tooltip title={downloading ? "Generowanie…" : "Pobierz PDF zaznaczonych"} placement="bottom">
              <button
                className={`${styles.filterBtn} ${downloading ? styles.filterBtnActive : ""}`}
                onClick={handleDownload}
                disabled={downloading}
              >
                <DownloadIcon />
              </button>
            </Tooltip>

            <div className={styles.savedFiltersBtn} onClick={(e) => openPopup(e, setPeriodPopupPos, periodPopupPos)}>
              Okres
              <FontAwesomeIcon icon={periodPopupPos ? faChevronUp : faChevronDown} />
            </div>

            <DocTypeSelect value={docType} onChange={(v) => onDocTypeChange?.(v)} />


            <div className={styles.savedFiltersBtn} onClick={(e) => openPopup(e, setSavedFiltersPopupPos, savedFiltersPopupPos)}>
              Twoje filtry
              <FontAwesomeIcon icon={savedFiltersPopupPos ? faChevronUp : faChevronDown} />
            </div>

            <Tooltip title="Filtry" placement="bottom-end">
              <button
                className={`${styles.filterBtn} ${filterOpen ? styles.filterBtnActive : ""}`}
                onClick={() => setFilterOpen((v) => !v)}
              >
                <FilterIcon />
                {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
              </button>
            </Tooltip>

            <Tooltip title="Kolumny" placement="bottom-end">
              <ColumnsPanelTrigger render={<ToolbarButton sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: "4px", padding: "4px 8px", color: "#5f5c71", minWidth: "unset", "&:hover": { borderColor: "#5d49c7", background: "rgba(93,73,199,0.04)", color: "#5d49c7" } }} />}>
                <ViewColumnIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </Tooltip>
          </div>
        </div>
      </Toolbar>
    );
  };

  if (
    !initialState ||
    !statusesData?.data ||
    !tagsData?.data ||
    !tags ||
    !statuses
  ) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
          initialState={{
            ...initialState,
            pinnedColumns: { right: ["Icons", "Menu"] },
          }}
          localeText={plGridLocales}
          slots={{
            toolbar: KSeFInvoiceToolbar,
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          showToolbar
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            columnsManagement: {
              getTogglableColumns,
            },
          }}
          loading={!isInitialized || isLoading}
          columnHeaderHeight={30}
          onRowsScrollEnd={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          onRowDoubleClick={(r) => {
            const itm = r.row as any;
            if (itm) {
              handleRowDoubleClick(itm);
            }
          }}
          columns={columnsMemo}
          hideFooter={true}
          rows={allRows}
          disableColumnFilter={true}
          disableAggregation={false}
          disablePivoting={true}
          onColumnVisibilityModelChange={(m) => {
            setColumnVisibilityModel(m);
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onColumnOrderChange={() => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onRowGroupingModelChange={(m) => {
            const newVisibility = { ...columnVisibilityModel };

            for (let i = 0; i < groupingModel.length; i++) {
              newVisibility[groupingModel[i]] = true;
            }

            for (let i = 0; i < m.length; i++) {
              newVisibility[m[i]] = false;
            }
            setColumnVisibilityModel(newVisibility);
            setGroupingModel(m);
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          aggregationModel={aggregationModel}
          columnVisibilityModel={columnVisibilityModel}
          onAggregationModelChange={() => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
        />
      </div>

      {/* Popup: Okres */}
      {periodPopupPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setPeriodPopupPos(null)} />
          <div className={styles.periodDropdown} style={{ position: "fixed", top: periodPopupPos.top, left: periodPopupPos.left, zIndex: 1400 }}>
            <PeriodPicker
              year={selectedYear}
              from={from}
              to={to}
              preset={preset}
              yearOptions={YEAR_OPTIONS}
              setYear={setSelectedYear}
              setFrom={setFrom}
              setTo={setTo}
              setPreset={setPreset}
            />
          </div>
        </>,
        document.body,
      )}

      {/* Popup: Twoje filtry */}
      {savedFiltersPopupPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setSavedFiltersPopupPos(null)} />
          <div className={styles.savedFiltersDropdown} style={{ position: "fixed", top: savedFiltersPopupPos.top, left: savedFiltersPopupPos.left, zIndex: 1400 }}>
            {savedFilters.length === 0 && <div className={styles.savedFiltersEmpty}>Brak zapisanych filtrów</div>}
            {savedFilters.map((f) => (
              <div key={f.Id} className={styles.savedFilterItem} onClick={() => setSavedFiltersPopupPos(null)}>
                <span className={styles.savedFilterName}>{f.Name}</span>
                <button className={styles.savedFilterDel} onClick={(e) => { e.stopPropagation(); persistFilters(savedFilters.filter((x) => x.Id !== f.Id)); }}>✕</button>
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}

      {/* Panel filtru (slide z prawej) */}
      {filterOpen && createPortal(
        <div className={styles.overlay} onClick={() => setFilterOpen(false)}>
          <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.panelHeader}>
              <span className={styles.panelTitle}>Filtry</span>
              <div className={styles.panelHeaderActions}>
                {activeFilterCount > 0 && (
                  <button className={styles.resetBtn} onClick={() => setActiveFilters(EMPTY_FILTERS)}>Wyczyść</button>
                )}
                <button className={styles.closeBtn} onClick={() => setFilterOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              {(["seller", "buyer", "typ"] as (keyof InvoiceFilters)[]).map((field) => {
                const labels: Record<keyof InvoiceFilters, string> = { seller: "Sprzedawca", buyer: "Nabywca", typ: "Typ dokumentu" };
                const options = filterOptions[field];
                const selected = activeFilters[field];
                const collapsed = collapsedSections[field] ?? false;
                return (
                  <div key={field} className={styles.filterSection}>
                    <button className={styles.filterSectionHeader} onClick={() => toggleSection(field)}>
                      <span className={styles.filterLabel}>
                        {labels[field]}
                        {selected.length > 0 && <span className={styles.filterSectionBadge}>{selected.length}</span>}
                      </span>
                      <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown} className={styles.filterChevron} />
                    </button>
                    {!collapsed && (
                      <>
                        {selected.length > 0 && (
                          <div className={styles.filterChipGroup}>
                            {selected.map((opt) => (
                              <Chip
                                key={opt}
                                label={opt}
                                size="small"
                                onDelete={() => toggleFilter(field, opt)}
                                sx={{
                                  fontStyle: opt === BRAK_STR ? "italic" : "normal",
                                  background: "#5d49c7",
                                  color: "#fff",
                                  "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } },
                                }}
                              />
                            ))}
                          </div>
                        )}
                        <Autocomplete
                          multiple
                          size="small"
                          options={options.filter((o) => !selected.includes(o))}
                          value={selected}
                          onChange={(_, newVal) => setActiveFilters((prev) => ({ ...prev, [field]: newVal }))}
                          disableCloseOnSelect
                          renderTags={() => null}
                          renderOption={(props, opt) => {
                            const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                            return <li key={key} {...rest} style={{ fontSize: 13, fontStyle: opt === BRAK_STR ? "italic" : "normal" }}>{opt}</li>;
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`Szukaj ${labels[field].toLowerCase()}…`}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 13 }, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#5d49c7" } }}
                            />
                          )}
                          noOptionsText="Brak wyników"
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showPreview && selectedInvoice && (
        <div className={styles.modalBg}>
          <PreviewModal
            itemId={selectedInvoice.KsefNumber}
            itemKind={ItemKind.KSeFInvoice}
            attachmentId={selectedInvoice.KsefNumber}
            refetch={() => {}}
            toggle={() => setShowPreview(false)}
            fileName={`faktura.pdf`}
          ></PreviewModal>
        </div>
      )}

      {metricOf && (
        <InvoiceMetricSlide
          invoice={metricOf}
          onClose={() => setMetricOf(null)}
        />
      )}
    </div>
  );
};

export default KSeFInvoiceRegister;
